import type {
  RoutePoint,
  ProcessedRoute,
  RouteStop,
  RouteSegment,
  RouteTrip,
  RouteTripGap,
  TripSplitReason,
  PlaybackFrame,
} from "../types/route";
import { ROUTE_THRESHOLDS } from "./constants";

interface TripSplitPoint {
  index: number;
  reason: TripSplitReason;
}

export interface ProcessRouteOptions {
  /** Vehicle or category max speed (km/h); scaled for teleport detection */
  maxSpeedKmh?: number | null;
}

export class RouteProcessor {
  static processRoute(
    bikeId: string,
    coordinates: RoutePoint[],
    options?: ProcessRouteOptions,
  ): ProcessedRoute | null {
    if (coordinates.length < 2) {
      return null;
    }

    const trips = this.splitIntoTrips(bikeId, coordinates, options);
    if (trips.length === 0) {
      return null;
    }

    return this.aggregateRouteStats(bikeId, trips);
  }

  static resolveTeleportMaxSpeedKmh(maxSpeedKmh?: number | null): number {
    if (
      maxSpeedKmh != null &&
      Number.isFinite(maxSpeedKmh) &&
      maxSpeedKmh > 0
    ) {
      return Math.max(
        maxSpeedKmh * ROUTE_THRESHOLDS.TELEPORT_SPEED_MULTIPLIER,
        ROUTE_THRESHOLDS.TELEPORT_SPEED_FLOOR_KMH,
      );
    }
    return ROUTE_THRESHOLDS.TELEPORT_MAX_SPEED_KMH;
  }

  private static splitIntoTrips(
    bikeId: string,
    coordinates: RoutePoint[],
    options?: ProcessRouteOptions,
  ): RouteTrip[] {
    const splitPoints = this.detectTripSplitPoints(coordinates, options);
    const trips: RouteTrip[] = [];

    for (let i = 0; i < splitPoints.length; i++) {
      const startIndex = splitPoints[i].index;
      const endIndex =
        i + 1 < splitPoints.length
          ? splitPoints[i + 1].index - 1
          : coordinates.length - 1;

      if (endIndex < startIndex) continue;

      const tripCoords = coordinates.slice(startIndex, endIndex + 1);
      if (tripCoords.length < 2) continue;

      const trip = this.buildTrip(
        bikeId,
        trips.length,
        tripCoords,
        splitPoints[i].reason,
      );
      trips.push(trip);
    }

    return trips;
  }

  private static detectTripSplitPoints(
    coordinates: RoutePoint[],
    options?: ProcessRouteOptions,
  ): TripSplitPoint[] {
    const splitMap = new Map<number, TripSplitReason>();
    splitMap.set(0, "start");
    const teleportMaxSpeedKmh = this.resolveTeleportMaxSpeedKmh(
      options?.maxSpeedKmh,
    );
    const minIntervalMinutes =
      ROUTE_THRESHOLDS.TELEPORT_MIN_INTERVAL_SECONDS / 60;

    for (let i = 1; i < coordinates.length; i++) {
      const prev = coordinates[i - 1];
      const curr = coordinates[i];

      const timeGapMinutes = this.getTimeDifferenceMinutes(
        prev.timestamp,
        curr.timestamp,
      );
      if (timeGapMinutes > ROUTE_THRESHOLDS.TIME_GAP_MINUTES) {
        splitMap.set(i, "time_gap");
        continue;
      }

      // Align with backend stats: skip speed check for zero/tiny Δt (dupes / jitter)
      if (timeGapMinutes <= minIntervalMinutes) {
        continue;
      }

      const distanceKm = this.haversineDistance(
        prev.latitude,
        prev.longitude,
        curr.latitude,
        curr.longitude,
      );

      // Sub-km jumps are almost always GPS noise, not vehicle transport
      if (distanceKm < ROUTE_THRESHOLDS.TELEPORT_MIN_DISTANCE_KM) {
        continue;
      }

      const impliedSpeed = distanceKm / (timeGapMinutes / 60);

      if (impliedSpeed > teleportMaxSpeedKmh) {
        splitMap.set(i, "teleport");
      }
    }

    // Long dwells are annotated as in-trip stops (detectStops), not trip
    // boundaries — splitting would draw a gap between consecutive GPS points
    // (often <1 min / ~100 m) that looks like a false break in the route.

    return Array.from(splitMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([index, reason]) => ({ index, reason }));
  }

  private static buildTrip(
    bikeId: string,
    tripIndex: number,
    coordinates: RoutePoint[],
    splitReason: TripSplitReason,
  ): RouteTrip {
    const stops = this.detectStops(coordinates);
    const movementSegments = this.analyzeSegments(coordinates);
    const analytics = this.calculateEnhancedAnalytics(
      coordinates,
      movementSegments,
      stops,
    );

    return {
      trip_index: tripIndex,
      bike_id: bikeId,
      coordinates,
      split_reason: splitReason,
      start_time: coordinates[0].timestamp,
      end_time: coordinates[coordinates.length - 1].timestamp,
      duration_minutes: analytics.totalDuration,
      moving_time_minutes: analytics.movingTime,
      hours_on_road: analytics.hoursOnRoad,
      total_distance_km: analytics.totalDistance,
      average_speed_kmh: analytics.averageSpeed,
      moving_average_speed_kmh: analytics.movingAverageSpeed,
      max_speed_kmh: analytics.maxSpeed,
      total_points: coordinates.length,
      stops,
      movement_segments: movementSegments,
    };
  }

  static buildTripGaps(trips: RouteTrip[]): RouteTripGap[] {
    const gaps: RouteTripGap[] = [];

    for (let i = 0; i < trips.length - 1; i++) {
      const fromTrip = trips[i];
      const toTrip = trips[i + 1];
      const fromPoint = fromTrip.coordinates[fromTrip.coordinates.length - 1];
      const toPoint = toTrip.coordinates[0];

      gaps.push({
        gap_index: i,
        from_trip_index: fromTrip.trip_index,
        to_trip_index: toTrip.trip_index,
        from_point: fromPoint,
        to_point: toPoint,
        reason: toTrip.split_reason,
        distance_km: this.haversineDistance(
          fromPoint.latitude,
          fromPoint.longitude,
          toPoint.latitude,
          toPoint.longitude,
        ),
        time_gap_minutes: this.getTimeDifferenceMinutes(
          fromPoint.timestamp,
          toPoint.timestamp,
        ),
      });
    }

    return gaps;
  }

  static getGapAnimationSteps(distanceKm: number): number {
    const scaled = Math.round(8 + distanceKm * 2);
    return Math.min(
      ROUTE_THRESHOLDS.GAP_ANIMATION_STEPS_MAX,
      Math.max(ROUTE_THRESHOLDS.GAP_ANIMATION_STEPS_MIN, scaled),
    );
  }

  static buildPlaybackFrames(route: ProcessedRoute): PlaybackFrame[] {
    const frames: PlaybackFrame[] = [];

    route.trips.forEach((trip, tripIndex) => {
      trip.coordinates.forEach((point, pointIndex) => {
        frames.push({
          type: "point",
          tripIndex,
          pointIndex,
          point,
        });
      });

      const gap = route.trip_gaps.find((g) => g.from_trip_index === tripIndex);
      if (gap) {
        const totalSteps = this.getGapAnimationSteps(gap.distance_km);
        for (let step = 1; step <= totalSteps; step++) {
          frames.push({
            type: "gap",
            gapIndex: gap.gap_index,
            step,
            totalSteps,
            from: gap.from_point,
            to: gap.to_point,
            reason: gap.reason,
          });
        }
      }
    });

    return frames;
  }

  static findNearestPoint(
    coordinates: RoutePoint[],
    lat: number,
    lng: number,
  ): { point: RoutePoint; index: number } {
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    coordinates.forEach((point, index) => {
      const distance = this.haversineDistance(
        lat,
        lng,
        point.latitude,
        point.longitude,
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return { point: coordinates[closestIndex], index: closestIndex };
  }

  static findFrameIndexForPoint(
    route: ProcessedRoute,
    tripIndex: number,
    pointIndex: number,
  ): number {
    const frames = this.buildPlaybackFrames(route);
    return frames.findIndex(
      (frame) =>
        frame.type === "point" &&
        frame.tripIndex === tripIndex &&
        frame.pointIndex === pointIndex,
    );
  }

  static interpolateGapPosition(
    from: RoutePoint,
    to: RoutePoint,
    step: number,
    totalSteps: number,
  ): { latitude: number; longitude: number } {
    const t = step / totalSteps;
    return {
      latitude: from.latitude + (to.latitude - from.latitude) * t,
      longitude: from.longitude + (to.longitude - from.longitude) * t,
    };
  }

  static getFramePosition(frame: PlaybackFrame): {
    latitude: number;
    longitude: number;
    timestamp: string;
    speed?: number;
    battery_level?: number;
    isInTransit: boolean;
    tripIndex?: number;
    gapReason?: TripSplitReason;
  } {
    if (frame.type === "point") {
      return {
        latitude: frame.point.latitude,
        longitude: frame.point.longitude,
        timestamp: frame.point.timestamp,
        speed: frame.point.speed,
        battery_level: frame.point.battery_level,
        isInTransit: false,
        tripIndex: frame.tripIndex,
      };
    }

    const pos = this.interpolateGapPosition(
      frame.from,
      frame.to,
      frame.step,
      frame.totalSteps,
    );
    return {
      ...pos,
      timestamp: frame.from.timestamp,
      speed: 0,
      isInTransit: true,
      gapReason: frame.reason,
    };
  }

  private static aggregateRouteStats(
    bikeId: string,
    trips: RouteTrip[],
  ): ProcessedRoute {
    const trip_gaps = this.buildTripGaps(trips);
    const coordinates = trips.flatMap((trip) => trip.coordinates);
    const stops = trips.flatMap((trip) => trip.stops);
    const segments = trips.flatMap((trip) => trip.movement_segments);

    const totalDistance = trips.reduce(
      (sum, trip) => sum + trip.total_distance_km,
      0,
    );
    const totalDuration = trips.reduce(
      (sum, trip) => sum + trip.duration_minutes,
      0,
    );
    const movingTime = trips.reduce(
      (sum, trip) => sum + trip.moving_time_minutes,
      0,
    );
    const maxSpeed = Math.max(...trips.map((trip) => trip.max_speed_kmh), 0);

    const averageSpeed =
      totalDuration > 0 ? (totalDistance / totalDuration) * 60 : 0;
    const movingAverageSpeed =
      movingTime > 0 ? (totalDistance / movingTime) * 60 : 0;

    return {
      bike_id: bikeId,
      trips,
      trip_gaps,
      coordinates,
      simplified: false,
      total_points: coordinates.length,
      total_trips: trips.length,
      start_time: trips[0].start_time,
      end_time: trips[trips.length - 1].end_time,
      duration_minutes: Math.round(totalDuration * 100) / 100,
      moving_time_minutes: Math.round(movingTime * 100) / 100,
      hours_on_road: Math.round((movingTime / 60) * 100) / 100,
      total_distance_km: Math.round(totalDistance * 1000) / 1000,
      average_speed_kmh: Math.round(averageSpeed * 100) / 100,
      moving_average_speed_kmh: Math.round(movingAverageSpeed * 100) / 100,
      max_speed_kmh: maxSpeed,
      stops,
      segments,
    };
  }

  private static detectStops(coordinates: RoutePoint[]): RouteStop[] {
    const stops: RouteStop[] = [];
    let stopStart: number | null = null;

    const finalizeStop = (stopEnd: number) => {
      if (stopStart === null || stopEnd < stopStart) return;

      const duration = this.getTimeDifferenceMinutes(
        coordinates[stopStart].timestamp,
        coordinates[stopEnd].timestamp,
      );

      if (duration >= ROUTE_THRESHOLDS.STOP_DURATION_MINUTES) {
        stops.push({
          start_time: coordinates[stopStart].timestamp,
          end_time: coordinates[stopEnd].timestamp,
          duration_minutes: duration,
          location: {
            latitude: coordinates[stopStart].latitude,
            longitude: coordinates[stopStart].longitude,
          },
          reason: this.determineStopReason(duration),
        });
      }
      stopStart = null;
    };

    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      const isStationary =
        (point.speed || 0) <= ROUTE_THRESHOLDS.STOP_SPEED_KMH;

      if (isStationary && stopStart === null) {
        stopStart = i;
      } else if (!isStationary && stopStart !== null) {
        finalizeStop(i - 1);
      }
    }

    if (stopStart !== null) {
      finalizeStop(coordinates.length - 1);
    }

    return stops;
  }

  private static analyzeSegments(coordinates: RoutePoint[]): RouteSegment[] {
    const segments: RouteSegment[] = [];
    let segmentStart = 0;
    let currentType: "moving" | "stopped" | "slow" = "moving";

    for (let i = 1; i < coordinates.length; i++) {
      const speed = coordinates[i].speed || 0;

      let newType: "moving" | "stopped" | "slow";
      if (speed <= ROUTE_THRESHOLDS.STOP_SPEED_KMH) {
        newType = "stopped";
      } else if (speed <= ROUTE_THRESHOLDS.SLOW_SPEED_KMH) {
        newType = "slow";
      } else {
        newType = "moving";
      }

      if (newType !== currentType || i === coordinates.length - 1) {
        if (i - segmentStart > 1) {
          const segment = this.createSegment(
            coordinates,
            segmentStart,
            i - 1,
            currentType,
          );
          segments.push(segment);
        }
        segmentStart = i - 1;
        currentType = newType;
      }
    }

    return segments;
  }

  private static createSegment(
    coordinates: RoutePoint[],
    startIndex: number,
    endIndex: number,
    type: "moving" | "stopped" | "slow",
  ): RouteSegment {
    const segmentPoints = coordinates.slice(startIndex, endIndex + 1);
    const distance = this.calculateDistance(segmentPoints);
    const duration = this.getTimeDifferenceMinutes(
      segmentPoints[0].timestamp,
      segmentPoints[segmentPoints.length - 1].timestamp,
    );

    const speeds = segmentPoints.map((p) => p.speed || 0).filter((s) => s > 0);

    return {
      start_index: startIndex,
      end_index: endIndex,
      distance_km: distance,
      duration_minutes: duration,
      average_speed_kmh:
        speeds.length > 0 ? speeds.reduce((a, b) => a + b) / speeds.length : 0,
      max_speed_kmh: speeds.length > 0 ? Math.max(...speeds) : 0,
      type,
    };
  }

  // Enhanced analytics calculation with proper moving time and speeds
  private static calculateEnhancedAnalytics(
    coordinates: RoutePoint[],
    segments: RouteSegment[],
    stops: RouteStop[],
  ) {
    const totalDistance = this.calculateDistance(coordinates);
    const totalDuration = this.calculateDuration(coordinates);

    // Calculate moving time (exclude stopped segments)
    const movingSegments = segments.filter(
      (s) => s.type === "moving" || s.type === "slow",
    );
    const movingTime = movingSegments.reduce(
      (total, segment) => total + segment.duration_minutes,
      0,
    );

    // Calculate total stop time
    const totalStopTime = stops.reduce(
      (total, stop) => total + stop.duration_minutes,
      0,
    );

    // All speed values excluding stationary points
    const allSpeeds = coordinates.map((p) => p.speed || 0).filter((s) => s > 0);

    // Moving speed values only (> STOP_SPEED_THRESHOLD)
    const movingSpeeds = coordinates
      .map((p) => p.speed || 0)
      .filter((s) => s > ROUTE_THRESHOLDS.STOP_SPEED_KMH);

    // Calculate different speed averages
    const overallAverageSpeed =
      totalDuration > 0 ? (totalDistance / totalDuration) * 60 : 0; // km/h
    const movingAverageSpeed =
      movingTime > 0 ? (totalDistance / movingTime) * 60 : 0; // km/h
    const speedBasedAverage =
      movingSpeeds.length > 0
        ? movingSpeeds.reduce((a, b) => a + b) / movingSpeeds.length
        : 0;

    return {
      totalDistance: Math.round(totalDistance * 1000) / 1000, // Round to 3 decimal places
      totalDuration,
      movingTime: Math.round(movingTime * 100) / 100, // Round to 2 decimal places
      hoursOnRoad: Math.round((movingTime / 60) * 100) / 100, // Convert to hours, round to 2 decimal places
      totalStopTime: Math.round(totalStopTime * 100) / 100,
      averageSpeed: Math.round(overallAverageSpeed * 100) / 100, // Overall average including stops
      movingAverageSpeed:
        Math.round(Math.max(movingAverageSpeed, speedBasedAverage) * 100) / 100, // Use the more reliable method
      maxSpeed: allSpeeds.length > 0 ? Math.max(...allSpeeds) : 0,
      movingSegmentCount: movingSegments.length,
      totalDataPoints: coordinates.length,
    };
  }

  private static calculateDistance(coordinates: RoutePoint[]): number {
    let totalDistance = 0;

    for (let i = 1; i < coordinates.length; i++) {
      const prev = coordinates[i - 1];
      const curr = coordinates[i];
      totalDistance += this.haversineDistance(
        prev.latitude,
        prev.longitude,
        curr.latitude,
        curr.longitude,
      );
    }

    return totalDistance;
  }

  static haversineDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private static calculateDuration(coordinates: RoutePoint[]): number {
    if (coordinates.length < 2) return 0;

    return this.getTimeDifferenceMinutes(
      coordinates[0].timestamp,
      coordinates[coordinates.length - 1].timestamp,
    );
  }

  private static getTimeDifferenceMinutes(
    startTime: string,
    endTime: string,
  ): number {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return (end.getTime() - start.getTime()) / (1000 * 60);
  }

  private static determineStopReason(
    durationMinutes: number,
  ): "parking" | "traffic" | "maintenance" | "unknown" {
    if (durationMinutes > 60) return "parking";
    if (durationMinutes > 10) return "maintenance";
    if (durationMinutes > 5) return "traffic";
    return "unknown";
  }

  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Route filtering methods
  static filterRoutesByTimeOfDay(
    routes: ProcessedRoute[],
    startTime: string,
    endTime: string,
  ): ProcessedRoute[] {
    return routes
      .map((route) => ({
        ...route,
        coordinates: route.coordinates.filter((point) => {
          const pointTime = new Date(point.timestamp);
          const timeString = pointTime.toTimeString().substring(0, 5); // HH:MM
          return timeString >= startTime && timeString <= endTime;
        }),
      }))
      .filter((route) => route.coordinates.length > 0);
  }

  static filterRoutesByDistance(
    routes: ProcessedRoute[],
    minDistance?: number,
    maxDistance?: number,
  ): ProcessedRoute[] {
    return routes.filter((route) => {
      if (minDistance && route.total_distance_km < minDistance) return false;
      if (maxDistance && route.total_distance_km > maxDistance) return false;
      return true;
    });
  }

  // Export utilities
  static exportToGPX(route: ProcessedRoute, trip?: RouteTrip): string {
    const trips = trip ? [trip] : route.trips;
    const trackSegments = trips
      .map((t) => {
        const points = t.coordinates
          .map(
            (point) => `
      <trkpt lat="${point.latitude}" lon="${point.longitude}">
        <time>${point.timestamp}</time>
        ${point.speed ? `<speed>${point.speed}</speed>` : ""}
      </trkpt>
    `,
          )
          .join("");
        return `<trkseg>${points}\n    </trkseg>`;
      })
      .join("\n    ");

    const startTime = trip?.start_time ?? route.start_time;
    const endTime = trip?.end_time ?? route.end_time;

    return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Vehicle Tracker">
  <trk>
    <name>Route ${route.bike_id}${trip ? ` Trip ${trip.trip_index + 1}` : ""}</name>
    <desc>Vehicle route from ${startTime} to ${endTime}</desc>
    ${trackSegments}
  </trk>
</gpx>`;
  }

  static exportToGeoJSON(route: ProcessedRoute, trip?: RouteTrip): string {
    const trips = trip ? [trip] : route.trips;

    const features = trips.map((t) => ({
      type: "Feature" as const,
      geometry: {
        type: "LineString" as const,
        coordinates: t.coordinates.map((point) => [
          point.longitude,
          point.latitude,
        ]),
      },
      properties: {
        bike_id: route.bike_id,
        trip_index: t.trip_index,
        split_reason: t.split_reason,
        start_time: t.start_time,
        end_time: t.end_time,
        duration_minutes: t.duration_minutes,
        moving_time_minutes: t.moving_time_minutes,
        hours_on_road: t.hours_on_road,
        total_distance_km: t.total_distance_km,
        average_speed_kmh: t.average_speed_kmh,
        moving_average_speed_kmh: t.moving_average_speed_kmh,
        max_speed_kmh: t.max_speed_kmh,
      },
    }));

    return JSON.stringify(
      {
        type: "FeatureCollection",
        features,
      },
      null,
      2,
    );
  }
}
